import { PageHeader, DataTable, LoadingState, FilterBar, ActionToolbar } from '../components/AdminPageUI';
import SummaryStrip, { SummaryMetric } from '../components/SummaryStrip';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  AccountBalanceWallet,
  Add,
  AttachMoney,
  CalendarToday,
  Delete,
  Edit,
  Print,
  Refresh,
  Savings,
  Summarize,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../utils/Api';
import { hasPermission } from '../utils/permissions';

const expenseCategories = [
  'General',
  'Feeding',
  'Utilities',
  'Salaries',
  'Maintenance',
  'Transport',
  'Learning Materials',
  'Office Supplies',
  'Health',
  'Security',
  'Other',
];

const paymentMethods = ['Cash', 'Mobile Money', 'Bank Transfer', 'Cheque', 'Other'];
const periodOptions = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'termly', label: 'Termly' },
];

const emptyForm = {
  title: '',
  category: 'General',
  amount: '',
  expenseDate: new Date().toISOString().slice(0, 10),
  periodType: 'weekly',
  paymentMethod: 'Cash',
  vendor: '',
  notes: '',
};

const formatCurrency = (value) => (
  `GHS ${(Number(value) || 0).toLocaleString('en-GH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
);

const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatDateForInput = (value) => {
  if (!value) return new Date().toISOString().slice(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return new Date().toISOString().slice(0, 10);
  return date.toISOString().slice(0, 10);
};

const getTermLabel = (term) => {
  if (!term) return 'Select Term';
  return `${term.term || 'Term'}${term.academicYear ? ` - ${term.academicYear}` : ''}`;
};

const getInitials = (name = '') => (
  String(name)
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || 'EX'
);

const extractWeekNumber = (value) => {
  const match = String(value ?? '').match(/\d+/);
  return match ? match[0] : '';
};

const formatWeekLabel = (value) => {
  const weekNumber = extractWeekNumber(value);
  return weekNumber ? `Week ${Number(weekNumber)}` : '';
};

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatUserLabel = (user) => {
  if (!user) return 'N/A';
  if (typeof user === 'string') return 'Recorded User';
  const name = user.name || [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  if (name && user.email) return `${name} (${user.email})`;
  return name || user.email || 'Recorded User';
};

export default function ExpenseProfitPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const now = useMemo(() => new Date(), []);

  const [schoolId, setSchoolId] = useState('');
  const [terms, setTerms] = useState([]);
  const [selectedTermId, setSelectedTermId] = useState('');
  const [periodType, setPeriodType] = useState('weekly');
  const [week, setWeek] = useState('Week 1');
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [year, setYear] = useState(String(now.getFullYear()));
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showAuditDialog, setShowAuditDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [viewingExpense, setViewingExpense] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const selectedTerm = useMemo(
    () => terms.find((term) => String(term._id) === String(selectedTermId)),
    [selectedTermId, terms],
  );

  const periodLabel = useMemo(() => {
    if (periodType === 'weekly') {
      return `${getTermLabel(selectedTerm)} / ${formatWeekLabel(week) || 'Week 1'}`;
    }
    if (periodType === 'monthly') {
      const date = new Date(Number(year), Number(month) - 1, 1);
      return date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    }
    return getTermLabel(selectedTerm);
  }, [month, periodType, selectedTerm, week, year]);

  const income = summary?.income || {};
  const expenses = summary?.expenses || {};
  const profit = Number(summary?.profit) || 0;
  const salariesPaid = (expenses.records || []).reduce(
    (total, expense) => total + (expense.source === 'payroll' ? Number(expense.amount) || 0 : 0), 0,
  );
  const canEditExpenses = hasPermission('canEditFees');
  const expenseRecords = useMemo(
    () => [...(summary?.expenses?.records || [])].sort(
      (a, b) => new Date(a.expenseDate || 0) - new Date(b.expenseDate || 0)
    ),
    [summary?.expenses?.records],
  );
  const expenseCategoryRows = summary?.expenses?.byCategory || [];

  const loadSchoolContext = useCallback(async () => {
    try {
      const res = await api.get('/school-info');
      const school = res.data?.school;
      setSchoolId(school?._id || school || '');
    } catch (err) {
      console.error('Failed to load school info:', err);
      toast.error('Unable to load school information.');
    }
  }, []);

  const loadTerms = useCallback(async () => {
    if (!schoolId) return;

    try {
      setInitializing(true);
      const [termsRes, currentWeekRes] = await Promise.all([
        api.get('/term/all', { params: { school: schoolId } }),
        api.get('/term/current-week', { params: { school: schoolId } }).catch(() => null),
      ]);

      const rawTerms = termsRes.data?.allTerms || [];
      const sortedTerms = [...rawTerms].sort((a, b) => {
        if (a.isCurrent && !b.isCurrent) return -1;
        if (!a.isCurrent && b.isCurrent) return 1;
        return new Date(b.startDate || 0) - new Date(a.startDate || 0);
      });
      setTerms(sortedTerms);

      const currentData = currentWeekRes?.data?.data || currentWeekRes?.data;
      const currentTerm = sortedTerms.find((term) => term.isCurrent)
        || sortedTerms.find((term) => (
          currentData?.termName
          && term.term === currentData.termName
          && (!currentData.academicYear || term.academicYear === currentData.academicYear)
        ))
        || sortedTerms[0];

      setSelectedTermId((current) => current || currentTerm?._id || '');
      setWeek((current) => formatWeekLabel(current) || formatWeekLabel(currentData?.weekNumber || 1) || 'Week 1');
    } catch (err) {
      console.error('Failed to load financial periods:', err);
      toast.error('Unable to load academic terms.');
    } finally {
      setInitializing(false);
    }
  }, [schoolId]);

  const fetchTermWeeks = useCallback(async () => {
    if (periodType !== 'weekly') {
      setAvailableWeeks([]);
      return;
    }

    if (!selectedTermId || !schoolId) {
      setAvailableWeeks([]);
      setWeek('');
      return;
    }

    try {
      const res = await api.get('/term/weeks', {
        params: {
          school: schoolId,
          academicYear: selectedTerm?.academicYear,
          term: selectedTerm?.term,
          termId: selectedTermId,
        },
      });

      if (!res.data?.weeks) {
        throw new Error('Invalid weeks data format');
      }

      const weeksData = [...res.data.weeks].sort((a, b) => a.weekNumber - b.weekNumber);
      const weeks = weeksData
        .map((item) => item.label || formatWeekLabel(item.weekNumber))
        .filter(Boolean);
      const currentActiveWeek = weeksData.find((item) => item.isCurrent);
      const currentWeekLabel = currentActiveWeek?.label || formatWeekLabel(currentActiveWeek?.weekNumber);

      setAvailableWeeks(weeks);
      setWeek((current) => {
        const currentLabel = formatWeekLabel(current);
        if (!weeks.length) return '';
        if (currentLabel && weeks.includes(currentLabel)) return currentLabel;
        return currentWeekLabel || weeks[0];
      });
    } catch (err) {
      console.error('Failed to fetch weeks:', err);
      setAvailableWeeks([]);
      setWeek('');
      toast.error('Unable to load weeks. Please try again.');
    }
  }, [periodType, schoolId, selectedTerm?.academicYear, selectedTerm?.term, selectedTermId]);

  const loadSummary = useCallback(async () => {
    if (periodType === 'weekly' && (!selectedTermId || !week)) {
      setSummary(null);
      return;
    }

    if (periodType === 'termly' && !selectedTermId) {
      setSummary(null);
      return;
    }

    try {
      setLoading(true);
      setError('');
      const params = { periodType };

      if (selectedTermId) params.termId = selectedTermId;
      if (periodType === 'weekly') params.week = extractWeekNumber(week) || 1;
      if (periodType === 'monthly') {
        params.month = month;
        params.year = year;
      }

      const res = await api.get('/expense-profit/summary', { params });
      setSummary(res.data);
    } catch (err) {
      console.error('Failed to load expense and profit summary:', err);
      const message = err.response?.data?.message || 'Failed to load expense and profit summary.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [month, periodType, selectedTermId, week, year]);

  useEffect(() => {
    loadSchoolContext();
  }, [loadSchoolContext]);

  useEffect(() => {
    loadTerms();
  }, [loadTerms]);

  useEffect(() => {
    fetchTermWeeks();
  }, [fetchTermWeeks]);

  useEffect(() => {
    if (!initializing) {
      loadSummary();
    }
  }, [initializing, loadSummary]);

  const openCreateDialog = () => {
    setEditingExpense(null);
    setForm({
      ...emptyForm,
      periodType,
      expenseDate: new Date().toISOString().slice(0, 10),
    });
    setDialogOpen(true);
  };

  const openEditDialog = (expense) => {
    if (expense.source === 'payroll') return;
    setEditingExpense(expense);
    setForm({
      title: expense.title || '',
      category: expense.category || 'General',
      amount: expense.amount || '',
      expenseDate: formatDateForInput(expense.expenseDate),
      periodType: expense.periodType || periodType,
      paymentMethod: expense.paymentMethod || 'Cash',
      vendor: expense.vendor || '',
      notes: expense.notes || '',
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingExpense(null);
    setForm(emptyForm);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveExpense = async () => {
    if (!form.title.trim()) {
      toast.warning('Expense title is required.');
      return;
    }

    if (!Number(form.amount) || Number(form.amount) <= 0) {
      toast.warning('Enter a valid expense amount.');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...form,
        title: form.title.trim(),
        category: form.category || 'General',
        amount: Number(form.amount),
        termId: selectedTermId || null,
        week: periodType === 'weekly' ? Number(extractWeekNumber(week) || 1) : null,
        periodType,
      };

      if (editingExpense?._id) {
        await api.put(`/expense-profit/expenses/${editingExpense._id}`, payload);
        toast.success('Expense updated.');
      } else {
        await api.post('/expense-profit/expenses', payload);
        toast.success('Expense recorded.');
      }

      closeDialog();
      loadSummary();
    } catch (err) {
      console.error('Failed to save expense:', err);
      toast.error(err.response?.data?.message || 'Failed to save expense.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteExpense = async (expense) => {
    if (expense.source === 'payroll') return;
    const confirmed = window.confirm(`Delete "${expense.title}" from ${formatDate(expense.expenseDate)}?`);
    if (!confirmed) return;

    try {
      await api.delete(`/expense-profit/expenses/${expense._id}`);
      toast.success('Expense deleted.');
      loadSummary();
    } catch (err) {
      console.error('Failed to delete expense:', err);
      toast.error(err.response?.data?.message || 'Failed to delete expense.');
    }
  };

  const handlePrintAuditReport = () => {
    const previousTitle = document.title;
    document.title = `Expense Profit Audit Report - ${periodLabel}`;
    window.print();
    setTimeout(() => {
      document.title = previousTitle;
    }, 1000);
  };

  const MobileExpenseCard = ({ expense }) => (
    <Card onClick={() => setViewingExpense(expense)} sx={{ mb: 2, boxShadow: 2, cursor: 'pointer', border: `1px solid ${theme.palette.divider}` }}>
      <CardContent sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Avatar sx={{ bgcolor: theme.palette.error.main, width: 40, height: 40 }}>
                {getInitials(expense.category || expense.title)}
              </Avatar>
              <Box>
                <Button variant="text" onClick={() => setViewingExpense(expense)} sx={{ p: 0, textAlign: 'left', textTransform: 'none', justifyContent: 'flex-start', fontWeight: 600 }}>
                  {expense.title}
                </Button>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={expense.category || 'General'} size="small" color="primary" variant="outlined" />
                  <Chip label={formatDate(expense.expenseDate)} size="small" variant="outlined" />
                </Stack>
              </Box>
            </Stack>
            <Typography variant="subtitle1" fontWeight="700" color="error.main">
              {formatCurrency(expense.amount)}
            </Typography>
          </Stack>

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                PAYMENT METHOD
              </Typography>
              <Typography variant="body2" fontWeight="500">
                {expense.paymentMethod || 'Cash'}
              </Typography>
            </Box>
            {expense.vendor && (
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  VENDOR
                </Typography>
                <Typography variant="body2" fontWeight="500">
                  {expense.vendor}
                </Typography>
              </Box>
            )}
          </Stack>

          {expense.source === 'payroll' && <Chip label="Linked Payroll" color="primary" size="small" variant="outlined" sx={{ alignSelf: 'flex-start' }} />}
          {canEditExpenses && expense.source !== 'payroll' && (
            <ActionToolbar direction="row" spacing={1}>
              <Button fullWidth size="small" variant="outlined" startIcon={<Edit />} onClick={(event) => { event.stopPropagation(); openEditDialog(expense); }}>
                Edit
              </Button>
              <Button fullWidth size="small" color="error" variant="outlined" startIcon={<Delete />} onClick={(event) => { event.stopPropagation(); handleDeleteExpense(expense); }}>
                Delete
              </Button>
            </ActionToolbar>
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 2 }}>
      {/* Header */}
      <PageHeader title={<>Expenses & Profit Management</>} description={<>Track income, expenses, and profit across school financial periods</>} actions={<>{canEditExpenses && <Button variant="contained" color="success" startIcon={<Add />} onClick={openCreateDialog} size={isMobile ? 'medium' : 'large'}>
                          Record Expense
                        </Button>}
        <Button variant="outlined" color="info" startIcon={<Summarize />} onClick={() => setShowAuditDialog(true)} disabled={loading || initializing || !summary} size={isMobile ? 'medium' : 'large'}>
                        Audit Report
                      </Button></>}></PageHeader>
      <Card
        sx={{
          mb: 3,
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          border: `1px solid ${theme.palette.primary.main}`,
        }}
      >
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" mb={2} spacing={1}>
            <Box>
              <Typography
                variant={isMobile ? 'subtitle1' : 'h6'}
                color="primary.main"
                fontWeight="bold"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <AccountBalanceWallet fontSize={isMobile ? 'small' : 'medium'} /> Financial Summary
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {periodLabel} {summary?.period ? `• ${formatDate(summary.period.startDate)} to ${formatDate(summary.period.endDate)}` : ''}
              </Typography>
            </Box>
            <Chip
              label={`Profit: ${formatCurrency(profit)}`}
              color={profit >= 0 ? 'success' : 'error'}
              size={isMobile ? 'small' : 'medium'}
              sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.85rem' : '1rem' }}
            />
          </Stack>

          <SummaryStrip container spacing={1} justifyContent="center" columns={{ xs: 12, sm: 6, lg: 12 }}>
            {[
              { label: 'SCHOOL FEES', value: income.schoolFees, color: 'primary' },
              { label: 'FEEDING FEES', value: income.feedingFees, color: 'warning' },
              { label: 'TOTAL INCOME', value: income.total, color: 'success' },
              { label: 'SALARIES PAID', value: salariesPaid, color: 'error', description: 'Included in expenses' },
              { label: 'EXPENSES', value: expenses.total, color: 'error' },
              { label: 'PROFIT', value: profit, color: profit >= 0 ? 'success' : 'error' },
            ].map((item) => (
              <Grid size={{ xs: 6, sm: 2, lg: 2 }} key={item.label}>
                <SummaryMetric
                  elevation={0}
                  sx={{
                    p: isMobile ? 0.75 : 1.5,
                    height: '100%',
                    boxSizing: 'border-box',
                    textAlign: 'center',
                    bgcolor: 'background.paper',
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="caption" display="block" color="text.secondary" fontWeight="bold">
                    {item.label}
                  </Typography>
                  <Typography
                    variant="h4"
                    color={`${item.color}.main`}
                    fontWeight="bold"
                    sx={{ fontSize: isMobile ? '0.85rem' : undefined }}
                  >
                    {formatCurrency(item.value)}
                  </Typography>
                  {item.description && <Typography variant="caption" color="text.secondary">{item.description}</Typography>}
                </SummaryMetric>
              </Grid>
            ))}
          </SummaryStrip>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Filter Card */}
      <FilterBar
        sx={{
          mb: isMobile ? 3 : 4,
          boxShadow: isMobile ? 1 : 3,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <CardContent sx={{ p: isMobile ? 2 : 4 }}>
          <Stack
            direction={isMobile ? 'column' : 'row'}
            alignItems={isMobile ? 'flex-start' : 'center'}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar
                sx={{
                  bgcolor: theme.palette.primary.main,
                  width: isMobile ? 32 : 40,
                  height: isMobile ? 32 : 40,
                }}
              >
                <CalendarToday fontSize={isMobile ? 'small' : 'medium'} />
              </Avatar>
              <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="600">
                Financial Setup & Filters
              </Typography>
            </Stack>
          </Stack>

          <Grid container spacing={isMobile ? 2 : 3}>
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
              <TextField
                select
                fullWidth
                label="Report Type"
                value={periodType}
                onChange={(event) => {
                  setPeriodType(event.target.value);
                  if (event.target.value !== 'weekly') {
                    setAvailableWeeks([]);
                  }
                }}
                disabled={loading}
                InputLabelProps={{ shrink: true }}
                size={isMobile ? 'small' : 'medium'}
              >
                {periodOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {(periodType === 'weekly' || periodType === 'termly') && (
              <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                <TextField
                  select
                  fullWidth
                  label="Academic Term"
                  value={selectedTermId}
                  onChange={(event) => {
                    setSelectedTermId(event.target.value);
                    setWeek('');
                    setSummary(null);
                  }}
                  disabled={loading || initializing || terms.length === 0}
                  InputLabelProps={{ shrink: true }}
                  size={isMobile ? 'small' : 'medium'}
                >
                  <MenuItem value="" disabled>Select Academic Term</MenuItem>
                  {terms.map((termItem) => (
                    <MenuItem key={termItem._id} value={termItem._id}>
                      {getTermLabel(termItem)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            {periodType === 'weekly' && (
              <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                <TextField
                  select
                  fullWidth
                  label="Week"
                  value={availableWeeks.includes(week) ? week : ''}
                  onChange={(event) => setWeek(event.target.value)}
                  disabled={loading || initializing || availableWeeks.length === 0}
                  InputLabelProps={{ shrink: true }}
                  size={isMobile ? 'small' : 'medium'}
                >
                  <MenuItem value="" disabled>Select Week</MenuItem>
                  {availableWeeks.map((weekLabel) => (
                    <MenuItem key={weekLabel} value={weekLabel}>
                      {weekLabel}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            {periodType === 'monthly' && (
              <>
                <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                  <TextField
                    select
                    fullWidth
                    label="Month"
                    value={month}
                    onChange={(event) => setMonth(event.target.value)}
                    disabled={loading}
                    InputLabelProps={{ shrink: true }}
                    size={isMobile ? 'small' : 'medium'}
                  >
                    {Array.from({ length: 12 }, (_, index) => {
                      const value = String(index + 1);
                      const label = new Date(2024, index, 1).toLocaleDateString('en-GB', { month: 'long' });
                      return (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                  <TextField
                    fullWidth
                    label="Year"
                    type="number"
                    value={year}
                    onChange={(event) => setYear(event.target.value)}
                    disabled={loading}
                    InputLabelProps={{ shrink: true }}
                    size={isMobile ? 'small' : 'medium'}
                  />
                </Grid>
              </>
            )}

            <Grid size={{ xs: 12 }}>
              <ActionToolbar
                direction={isMobile ? 'column' : 'row'}
                spacing={2}
                alignItems={isMobile ? 'stretch' : 'center'}
              >
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Summarize />}
                  onClick={loadSummary}
                  disabled={loading || initializing || (periodType === 'weekly' && (!selectedTermId || !week))}
                  size={isMobile ? 'medium' : 'large'}
                >
                  Apply Filter
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={loadSummary}
                  disabled={loading || initializing || (periodType === 'weekly' && (!selectedTermId || !week))}
                  size={isMobile ? 'medium' : 'large'}
                >
                  Refresh
                </Button>
                
                
              </ActionToolbar>
            </Grid>
          </Grid>
        </CardContent>
      </FilterBar>

      {/* Financial Summary */}
      

      <Alert severity={profit >= 0 ? 'success' : 'warning'} sx={{ mb: 3 }} icon={<Summarize />}>
        <Typography variant="body2">
          Using <strong>School Fees</strong> and <strong>Feeding Fee</strong> collections against recorded school expenses, including <strong>paid payroll</strong>.
        </Typography>
      </Alert>

      {/* Expense Breakdown */}
      <Card
        sx={{
          mb: isMobile ? 3 : 4,
          boxShadow: isMobile ? 1 : 3,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <CardContent>
          <Typography variant={isMobile ? 'h6' : 'h6'} gutterBottom fontWeight="600">
            Expense Breakdown
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Spending grouped by category for the selected period.
          </Typography>

          {(expenses.byCategory || []).length === 0 ? (
            <Alert severity="info">No expenses recorded for this period.</Alert>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <DataTable><Table size="small" aria-label="Expenses by category">
                <TableHead>
                  <TableRow>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Share of expenses</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
              {(expenses.byCategory || []).map((item) => {
                const percentage = expenses.total ? Math.round((Number(item.amount) / expenses.total) * 100) : 0;
                return (
                  <TableRow key={item.category}>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>{item.category}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                      {formatCurrency(item.amount)}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1.5}>
                        <Box sx={{ width: 72, height: 4, borderRadius: 1, bgcolor: alpha(theme.palette.error.main, 0.1), overflow: 'hidden', display: { xs: 'none', sm: 'block' } }}>
                          <Box sx={{ width: `${percentage}%`, height: '100%', bgcolor: 'error.main' }} />
                        </Box>
                        <Typography variant="body2" sx={{ minWidth: 36 }}>{percentage}%</Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
                </TableBody>
              </Table></DataTable>
            </Box>
          )}
        </CardContent>
      </Card>

      {(expenses.records || []).some(expense => expense.source === 'payroll') &&
        (expenses.records || []).some(expense => expense.source !== 'payroll' && expense.category === 'Salaries') && (
          <Alert severity="warning" sx={{ mb: 2 }}>This period includes linked payroll and manually recorded salary expenses. Review the manual entries to ensure the same salary payment is not counted twice.</Alert>
        )}

      {/* Detailed Records */}
      <Card
        sx={{
          boxShadow: isMobile ? 1 : 3,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              p: isMobile ? 2 : 3,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Stack
              direction={isMobile ? 'column' : 'row'}
              justifyContent="space-between"
              alignItems={isMobile ? 'flex-start' : 'center'}
              spacing={isMobile ? 1 : 0}
            >
              <Typography variant={isMobile ? 'h6' : 'h6'} fontWeight="600">
                Expense Records ({(expenses.records || []).length})
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: isMobile ? 1 : 0, flexWrap: 'wrap' }} useFlexGap>
                <Chip
                  label={`Total: ${formatCurrency(expenses.total)}`}
                  color="error"
                  variant="outlined"
                  size={isMobile ? 'small' : 'medium'}
                />
                <Chip
                  label={`${expenses.count || 0} records`}
                  color="primary"
                  variant="outlined"
                  size={isMobile ? 'small' : 'medium'}
                />
                {canEditExpenses && (
                  <Button variant="outlined" size={isMobile ? 'small' : 'medium'} startIcon={<Add />} onClick={openCreateDialog}>
                    Add Expense
                  </Button>
                )}
              </Stack>
            </Stack>
          </Box>

          {loading || initializing ? (
            <LoadingState />
          ) : (expenses.records || []).length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
              <Savings
                sx={{
                  fontSize: isMobile ? 48 : 64,
                  color: 'text.secondary',
                  opacity: 0.5,
                  mb: 2,
                }}
              />
              <Typography variant={isMobile ? 'h6' : 'h6'} color="text.secondary" gutterBottom>
                No expense records found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Record expenses or change the financial period to view existing records.
              </Typography>
            </Box>
          ) : isMobile ? (
            <Box sx={{ p: 2 }}>
              <Stack spacing={2}>
                {(expenses.records || []).map((expense) => (
                  <MobileExpenseCard key={expense._id} expense={expense} />
                ))}
              </Stack>
            </Box>
          ) : (
            <Box
              sx={{
                width: '100%',
                overflow: 'auto',
                maxHeight: isTablet ? '60vh' : 'none',
              }}
            >
              <DataTable><Table sx={{ minWidth: isTablet ? 800 : 'auto' }}>
                <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>DATE</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>EXPENSE</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>CATEGORY</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>METHOD</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }} align="right">AMOUNT</TableCell>
                    {canEditExpenses && <TableCell sx={{ fontWeight: 'bold', py: 2 }} align="right">ACTIONS</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(expenses.records || []).map((expense) => (
                    <TableRow
                      key={expense._id}
                      onClick={() => setViewingExpense(expense)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.02),
                        },
                      }}
                    >
                      <TableCell sx={{ py: 2 }}>{formatDate(expense.expenseDate)}</TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar sx={{ bgcolor: theme.palette.error.main, width: 40, height: 40 }}>
                            {getInitials(expense.category || expense.title)}
                          </Avatar>
                          <Box>
                            <Button variant="text" onClick={() => setViewingExpense(expense)} sx={{ p: 0, textAlign: 'left', textTransform: 'none', justifyContent: 'flex-start', fontWeight: 500 }}>
                              {expense.title}
                            </Button>
                            {expense.vendor && (
                              <Typography variant="caption" color="text.secondary">
                                Vendor: {expense.vendor}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Chip label={expense.category || 'General'} size="small" color="primary" variant="outlined" />
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>{expense.paymentMethod || 'Cash'}</TableCell>
                      <TableCell sx={{ py: 2 }} align="right">
                        <Typography variant="subtitle1" fontWeight="500" color="error.main">
                          {formatCurrency(expense.amount)}
                        </Typography>
                      </TableCell>
                      {canEditExpenses && (
                        <TableCell sx={{ py: 2 }} align="right">
                          {expense.source === 'payroll' ? <Chip label="Linked Payroll" size="small" variant="outlined" color="primary" /> : <>
                          <Tooltip title="Edit expense">
                            <IconButton onClick={(event) => { event.stopPropagation(); openEditDialog(expense); }} size="small">
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete expense">
                            <IconButton onClick={(event) => { event.stopPropagation(); handleDeleteExpense(expense); }} color="error" size="small">
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          </>}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table></DataTable>
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(viewingExpense)} onClose={() => setViewingExpense(null)} fullWidth maxWidth="sm" aria-labelledby="expense-details-title">
        <DialogTitle id="expense-details-title">Expense Details</DialogTitle>
        {viewingExpense && (
          <DialogContent dividers>
            <Typography variant="h6" sx={{ overflowWrap: 'anywhere' }}>{viewingExpense.title}</Typography>
            <Typography variant="h5" color="error.main" fontWeight="bold" sx={{ mt: 1, mb: 3 }}>
              {formatCurrency(viewingExpense.amount)}
            </Typography>
            <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              {[
                ['Category', viewingExpense.category || 'General'],
                ['Expense Date', formatDate(viewingExpense.expenseDate)],
                ['Payment Method', viewingExpense.paymentMethod || 'Cash'],
                ['Vendor / Receiver', viewingExpense.vendor || 'Not recorded'],
                ['Reporting Period', viewingExpense.payrollDetails?.month || periodOptions.find(option => option.value === viewingExpense.periodType)?.label || 'Not recorded'],
                ['Academic Term', (() => {
                  const term = terms.find(item => String(item._id) === String(viewingExpense.termId?._id || viewingExpense.termId));
                  return term ? getTermLabel(term) : 'Not recorded';
                })()],
                ['Week', formatWeekLabel(viewingExpense.week) || 'Not applicable'],
                ['Recorded By', formatUserLabel(viewingExpense.recordedBy)],
                ['Recorded At', formatDateTime(viewingExpense.createdAt)],
                ['Updated By', formatUserLabel(viewingExpense.updatedBy)],
                ['Last Updated', formatDateTime(viewingExpense.updatedAt)],
              ].map(([label, value]) => (
                <Box key={label}>
                  <Typography component="dt" variant="caption" color="text.secondary">{label}</Typography>
                  <Typography component="dd" variant="body2" sx={{ m: 0, overflowWrap: 'anywhere' }}>{value}</Typography>
                </Box>
              ))}
            </Box>
            {viewingExpense.payrollDetails && (
              <Box sx={{ mt: 3 }}>
                <Alert severity="info" sx={{ mb: 2 }}>Linked to paid payroll. Only net payments count toward expenses; deductions are shown separately. Changes are managed through payroll.</Alert>
                <Typography variant="subtitle2">Gross: {formatCurrency(viewingExpense.payrollDetails.totalGross)}</Typography>
                <Typography variant="subtitle2">Deductions: {formatCurrency(viewingExpense.payrollDetails.totalDeductions)}</Typography>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>Net Paid: {formatCurrency(viewingExpense.payrollDetails.totalNet)}</Typography>
                <Box sx={{ overflowX: 'auto' }}>
                  <DataTable><Table size="small" aria-label="Payroll staff breakdown">
                    <TableHead><TableRow>
                      <TableCell>Staff</TableCell><TableCell>Type</TableCell><TableCell align="right">Gross</TableCell><TableCell align="right">Deductions</TableCell><TableCell align="right">Net Paid</TableCell>
                    </TableRow></TableHead>
                    <TableBody>{viewingExpense.payrollDetails.staff.map(staff => (
                      <TableRow key={staff.id}>
                        <TableCell>{staff.name}</TableCell><TableCell>{staff.staffType}</TableCell>
                        <TableCell align="right">{formatCurrency(staff.grossSalary)}</TableCell>
                        <TableCell align="right">{formatCurrency(staff.deductions)}</TableCell>
                        <TableCell align="right">{formatCurrency(staff.netSalary)}</TableCell>
                      </TableRow>
                    ))}</TableBody>
                  </Table></DataTable>
                </Box>
              </Box>
            )}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>Notes</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {viewingExpense.notes || 'No notes recorded.'}
              </Typography>
            </Box>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setViewingExpense(null)}>Close</Button>
          {canEditExpenses && viewingExpense && viewingExpense.source !== 'payroll' && (
            <Button variant="contained" startIcon={<Edit />} onClick={() => {
              const expense = viewingExpense;
              setViewingExpense(null);
              openEditDialog(expense);
            }}>Edit Expense</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Audit Report Dialog */}
      <Dialog
        open={showAuditDialog}
        onClose={() => setShowAuditDialog(false)}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1}
          >
            <Typography variant="h6" fontWeight="bold">
              Expense & Profit Audit Report
            </Typography>
            <Chip
              label={periodLabel}
              color="primary"
              variant="outlined"
              size={isMobile ? 'small' : 'medium'}
            />
          </Stack>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            bgcolor: 'background.paper',
            p: isMobile ? 1 : 4,
            '@media print': { p: 0, m: 0, border: 'none' },
          }}
        >
          <style>
            {`
              @media print {
                body * { visibility: hidden; }
                #printable-expense-profit-audit, #printable-expense-profit-audit * { visibility: visible; }
                #printable-expense-profit-audit { position: absolute; left: 0; top: 0; width: 100%; padding: 0 !important; }
                .MuiDialogActions-root { display: none !important; }
                .MuiDialogTitle-root { display: none !important; }
                .MuiDialog-paper { box-shadow: none !important; margin: 0 !important; width: 100% !important; max-width: none !important; max-height: none !important; }
                #printable-expense-profit-audit td,
                #printable-expense-profit-audit th,
                #printable-expense-profit-audit p,
                #printable-expense-profit-audit span,
                #printable-expense-profit-audit .MuiTypography-root,
                #printable-expense-profit-audit .MuiTableCell-root {
                  color: #000 !important;
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
              }
            `}
          </style>

          <Box id="printable-expense-profit-audit">
            {!summary ? (
              <LoadingState />
            ) : (
              <Stack spacing={4} sx={{ color: '#000' }}>
                <Box sx={{ textAlign: 'center', borderBottom: '2px solid #000', pb: 2 }}>
                  <Typography variant="h5" fontWeight="bold" textTransform="uppercase">
                    Expense & Profit Audit Report
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Report Period: {periodLabel}
                  </Typography>
                  <Typography variant="subtitle2">
                    Date Range: {summary?.period ? `${formatDate(summary.period.startDate)} to ${formatDate(summary.period.endDate)}` : 'N/A'}
                  </Typography>
                  <Typography variant="caption" display="block">
                    Generated On: {formatDateTime(new Date())}
                  </Typography>
                </Box>

                <Paper elevation={0} sx={{ border: '1px solid #000', borderRadius: 0 }}>
                  <DataTable><Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f0f0f0' }}>
                        <TableCell colSpan={6} align="center" sx={{ borderBottom: '1px solid #000', fontWeight: 'bold' }}>
                          FINANCIAL SUMMARY
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #ccc' }}>School Fees</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #ccc' }}>Feeding Fees</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #ccc' }}>Total Income</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #ccc' }}>Salaries Paid (Included in Expenses)</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #ccc' }}>Total Expenses</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Profit</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" sx={{ borderRight: '1px solid #ccc' }}>{formatCurrency(income.schoolFees)}</TableCell>
                        <TableCell align="center" sx={{ borderRight: '1px solid #ccc' }}>{formatCurrency(income.feedingFees)}</TableCell>
                        <TableCell align="center" sx={{ borderRight: '1px solid #ccc', fontWeight: 'bold' }}>{formatCurrency(income.total)}</TableCell>
                        <TableCell align="center" sx={{ borderRight: '1px solid #ccc', fontWeight: 'bold' }}>{formatCurrency(salariesPaid)}</TableCell>
                        <TableCell align="center" sx={{ borderRight: '1px solid #ccc', fontWeight: 'bold' }}>{formatCurrency(expenses.total)}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>{formatCurrency(profit)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table></DataTable>
                </Paper>

                <Paper elevation={0} sx={{ border: '1px solid #000', borderRadius: 0 }}>
                  <DataTable><Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f0f0f0' }}>
                        <TableCell colSpan={3} align="center" sx={{ borderBottom: '1px solid #000', fontWeight: 'bold' }}>
                          EXPENSE CATEGORY BREAKDOWN
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #666' }}>Category</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', borderBottom: '1px solid #666' }}>Share</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', borderBottom: '1px solid #666' }}>Amount (GHS)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {expenseCategoryRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} align="center">No expense categories recorded.</TableCell>
                        </TableRow>
                      ) : (
                        expenseCategoryRows.map((item) => {
                          const percentage = expenses.total ? Math.round((Number(item.amount) / expenses.total) * 100) : 0;
                          return (
                            <TableRow key={item.category}>
                              <TableCell>{item.category}</TableCell>
                              <TableCell align="center">{percentage}%</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                {formatCurrency(item.amount)}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table></DataTable>
                </Paper>

                <Paper elevation={0} sx={{ border: '1px solid #000', borderRadius: 0 }}>
                  <DataTable><Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f0f0f0' }}>
                        <TableCell colSpan={8} align="center" sx={{ borderBottom: '1px solid #000', fontWeight: 'bold' }}>
                          EXPENSE LEDGER
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #666' }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #666' }}>Expense</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #666' }}>Category</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #666' }}>Vendor</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #666' }}>Method</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #666' }}>Recorded By</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #666' }}>Recorded At</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', borderBottom: '1px solid #666' }}>Amount (GHS)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {expenseRecords.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center">No expense records found for this period.</TableCell>
                        </TableRow>
                      ) : (
                        expenseRecords.map((expense) => (
                          <TableRow key={expense._id}>
                            <TableCell>{formatDate(expense.expenseDate)}</TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="600">{expense.title}</Typography>
                              {expense.notes && (
                                <Typography variant="caption">Note: {expense.notes}</Typography>
                              )}
                            </TableCell>
                            <TableCell>{expense.category || 'General'}</TableCell>
                            <TableCell>{expense.vendor || 'N/A'}</TableCell>
                            <TableCell>{expense.paymentMethod || 'Cash'}</TableCell>
                            <TableCell>{formatUserLabel(expense.recordedBy)}</TableCell>
                            <TableCell>{formatDateTime(expense.createdAt)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(expense.amount)}</TableCell>
                          </TableRow>
                        ))
                      )}
                      <TableRow sx={{ bgcolor: '#f7f7f7' }}>
                        <TableCell colSpan={7} align="right" sx={{ fontWeight: 'bold' }}>
                          TOTAL EXPENSES
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(expenses.total)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table></DataTable>
                </Paper>

                <Box sx={{ mt: 6, pt: 4, display: 'flex', justifyContent: 'space-between', borderTop: '1px dashed #ccc' }}>
                  <Box sx={{ width: '30%', textAlign: 'center' }}>
                    <Box sx={{ borderBottom: '1px solid #000', height: '40px' }} />
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>Prepared By (Accountant)</Typography>
                    <Typography variant="caption">Date: _________________</Typography>
                  </Box>
                  <Box sx={{ width: '30%', textAlign: 'center' }}>
                    <Box sx={{ borderBottom: '1px solid #000', height: '40px' }} />
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>Approved By (Admin/Principal)</Typography>
                    <Typography variant="caption">Date: _________________</Typography>
                  </Box>
                </Box>
              </Stack>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAuditDialog(false)}>Close</Button>
          <Button variant="contained" startIcon={<Print />} onClick={handlePrintAuditReport} disabled={!summary}>
            Print / Download PDF
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogOpen}
        onClose={saving ? undefined : closeDialog}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 2 } }}
      >
        <DialogTitle
          sx={{
            bgcolor: theme.palette.primary.main,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: isMobile ? 2 : 3,
          }}
        >
          <AttachMoney /> {editingExpense ? 'Edit Expense' : 'Record Expense'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Expense Title"
              value={form.title}
              onChange={(event) => handleFormChange('title', event.target.value)}
              fullWidth
              required
            />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}   >
                <TextField
                  label="Amount"
                  type="number"
                  value={form.amount}
                  onChange={(event) => handleFormChange('amount', event.target.value)}
                  fullWidth
                  required
                  InputProps={{
                    startAdornment: <InputAdornment position="start">GHS</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}   >
                <TextField
                  label="Expense Date"
                  type="date"
                  value={form.expenseDate}
                  onChange={(event) => handleFormChange('expenseDate', event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  required
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}   >
                <TextField
                  select
                  label="Category"
                  value={form.category}
                  onChange={(event) => handleFormChange('category', event.target.value)}
                  fullWidth
                >
                  {expenseCategories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}   >
                <TextField
                  select
                  label="Payment Method"
                  value={form.paymentMethod}
                  onChange={(event) => handleFormChange('paymentMethod', event.target.value)}
                  fullWidth
                >
                  {paymentMethods.map((method) => (
                    <MenuItem key={method} value={method}>
                      {method}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            <TextField
              label="Vendor / Receiver"
              value={form.vendor}
              onChange={(event) => handleFormChange('vendor', event.target.value)}
              fullWidth
            />
            <TextField
              label="Notes"
              value={form.notes}
              onChange={(event) => handleFormChange('notes', event.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveExpense}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {saving ? 'Saving...' : editingExpense ? 'Update Expense' : 'Save Expense'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
